import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchSalesForSelect, type SaleOption } from '@/services/lookup';
import { fetchVariants, type VariantOption } from '@/services/variants';
import { createReturn } from '@/services/returns';

interface ReturnModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function ReturnModal({ onClose, onSaved }: ReturnModalProps) {
  const [sales, setSales] = useState<SaleOption[]>([]);
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const [saleId, setSaleId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [returnType, setReturnType] = useState<'full_return' | 'partial_return'>('partial_return');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSalesForSelect().then((r) => {
      setSales(r);
      if (r[0]) setSaleId(r[0].id);
    });
    fetchVariants().then((r) => setVariants(r.data));
  }, []);

  const suggestedRefund = useMemo(() => {
    const v = variants.find((x) => x.id === variantId);
    return v ? v.retailPrice * (Number(quantity) || 0) : 0;
  }, [variantId, quantity, variants]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!saleId) {
      setError('Selecciona la venta original.');
      return;
    }
    if (!variantId) {
      setError('Selecciona el producto devuelto.');
      return;
    }

    setSaving(true);
    const res = await createReturn({
      saleId,
      variantId,
      quantity: Math.max(1, Number(quantity) || 1),
      refundAmount: suggestedRefund,
      reason,
      returnType,
    });
    setSaving(false);

    if (!res.success) {
      setError(res.error || 'No se pudo crear la devolución.');
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal title="Nueva Devolución" subtitle="Registra una devolución de postventa." onClose={onClose} maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Venta original *</label>
          <select className={inputCls} value={saleId} onChange={(e) => setSaleId(e.target.value)}>
            {sales.map((s) => (
              <option key={s.id} value={s.id}>
                {s.saleNumber} · {s.customerName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Producto devuelto *</label>
          <select className={inputCls} value={variantId} onChange={(e) => setVariantId(e.target.value)}>
            <option value="">Selecciona variante...</option>
            {variants.map((v) => (
              <option key={v.id} value={v.id}>
                {v.productName} · {v.sizeName}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Cantidad</label>
            <input type="number" min="1" className={inputCls} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Tipo</label>
            <select className={inputCls} value={returnType} onChange={(e) => setReturnType(e.target.value as 'full_return' | 'partial_return')}>
              <option value="partial_return">Parcial</option>
              <option value="full_return">Total</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelCls}>Motivo</label>
          <textarea className={`${inputCls} resize-none`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Talla incorrecta, defecto, etc." />
        </div>

        <div className="flex items-center justify-between bg-accent-50 border border-accent-100 rounded-md px-4 py-3">
          <span className="text-sm text-foreground-700">Monto a reembolsar</span>
          <span className="text-lg font-bold text-foreground-950">${suggestedRefund.toLocaleString()}</span>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear devolución</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}